import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Plus, FileText, Mail, Download, Pencil, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';

export default function OffresTab({ clientId, offres, projets, client, onUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const [editingOffre, setEditingOffre] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    montant: '',
    validite: '',
    projet_id: '',
    statut: 'Envoyée',
    created_by: 'user@sesam.com'
  });

  const handleAdd = () => {
    setEditingOffre(null);
    setFormData({
      titre: '',
      description: '',
      montant: '',
      validite: '',
      projet_id: '',
      statut: 'Envoyée',
      created_by: 'user@sesam.com'
    });
    setShowModal(true);
  };

  const handleEdit = (offre) => {
    setEditingOffre(offre);
    setFormData({
      ...offre,
      montant: offre.montant.toString()
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.titre || !formData.montant) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        ...formData,
        montant: parseFloat(formData.montant)
      };

      if (editingOffre) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/offres/${editingOffre.id}`,
          payload
        );
        toast.success('Offre modifiée avec succès');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/offres`,
          payload
        );
        toast.success('Offre créée avec succès');
      }
      setShowModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving offre:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (offreId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/offres/${offreId}`);
      toast.success('Offre supprimée');
      onUpdate();
    } catch (error) {
      console.error('Error deleting offre:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleGeneratePDF = async (offre) => {
    try {
      toast.info('Génération du PDF en cours...');
      
      // Générer le contenu PDF (simplifié - vous pouvez utiliser une bibliothèque comme jsPDF)
      const pdfContent = generateSimplePDF(offre, client);
      
      // Créer un lien de téléchargement
      const blob = new Blob([pdfContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `offre_${client.entreprise}_${offre.id}.txt`;
      link.click();
      
      toast.success('PDF généré avec succès');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleSendEmail = async (offre) => {
    try {
      toast.info('Envoi de l\'offre par email...');
      
      // TODO: Implémenter l'envoi via Resend
      // Pour l'instant, simulation
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mettre à jour le statut
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/offres/${offre.id}`,
        { ...offre, email_envoye: true, date_envoi: new Date().toISOString() }
      );
      
      toast.success('Offre envoyée par email');
      onUpdate();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const generateSimplePDF = (offre, client) => {
    return `
===========================================
PROPOSITION COMMERCIALE
===========================================

Client: ${client.entreprise}
Adresse: ${client.adresse}
Téléphone: ${client.telephone}

-------------------------------------------

${offre.titre}

${offre.description}

Montant: ${offre.montant.toLocaleString()} GNF
Validité: ${new Date(offre.validite).toLocaleDateString()}

-------------------------------------------

CRM SESAM
Service aux Sociétés Africaines en Marketing
Contact: info@sesam.com
`;
  };

  const getStatutBadge = (statut) => {
    const colors = {
      'Envoyée': 'bg-blue-100 text-blue-700',
      'Acceptée': 'bg-green-100 text-green-700',
      'Refusée': 'bg-red-100 text-red-700',
      'En négociation': 'bg-yellow-100 text-yellow-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Offres Commerciales</h3>
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          Nouvelle Offre
        </Button>
      </div>

      {offres.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            Aucune offre créée.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {offres.map((offre) => {
            const projet = projets.find(p => p.id === offre.projet_id);
            return (
              <Card key={offre.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getStatutBadge(offre.statut)}>
                          {offre.statut}
                        </Badge>
                        {offre.email_envoye && (
                          <Badge className="bg-green-100 text-green-700">
                            📧 Envoyée par email
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-bold text-xl mb-1">{offre.titre}</h4>
                      {projet && (
                        <p className="text-sm text-gray-600">
                          Projet lié: {projet.nom}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleEdit(offre)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDelete(offre.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{offre.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Montant:</span>
                      <span className="font-bold text-lg text-green-600">
                        {offre.montant.toLocaleString()} GNF
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Validité jusqu'au:</span>
                      <span className="font-semibold">
                        {new Date(offre.validite).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <span>Créée le {new Date(offre.date_creation).toLocaleDateString()}</span>
                    {offre.date_envoi && (
                      <span>• Envoyée le {new Date(offre.date_envoi).toLocaleDateString()}</span>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => handleGeneratePDF(offre)}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Générer PDF
                    </Button>
                    <Button
                      onClick={() => handleSendEmail(offre)}
                      className="gap-2 bg-blue-600 hover:bg-blue-700"
                      disabled={offre.email_envoye}
                    >
                      <Send className="h-4 w-4" />
                      {offre.email_envoye ? 'Déjà envoyée' : 'Envoyer par Email'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Ajout/Édition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingOffre ? 'Modifier l\'offre' : 'Nouvelle offre commerciale'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titre de l'offre *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Ex: Campagne publicitaire LED 2026"
              />
            </div>

            <div>
              <Label>Projet associé (optionnel)</Label>
              <Select
                value={formData.projet_id}
                onValueChange={(val) => setFormData({ ...formData, projet_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un projet" />
                </SelectTrigger>
                <SelectContent>
                  {projets.map((projet) => (
                    <SelectItem key={projet.id} value={projet.id}>
                      {projet.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détails de l'offre, prestations incluses..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant (GNF) *</Label>
                <Input
                  type="number"
                  value={formData.montant}
                  onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                  placeholder="45000000"
                />
              </div>
              <div>
                <Label>Date de validité *</Label>
                <Input
                  type="date"
                  value={formData.validite}
                  onChange={(e) => setFormData({ ...formData, validite: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Statut *</Label>
              <Select
                value={formData.statut}
                onValueChange={(val) => setFormData({ ...formData, statut: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Envoyée">Envoyée</SelectItem>
                  <SelectItem value="En négociation">En négociation</SelectItem>
                  <SelectItem value="Acceptée">Acceptée</SelectItem>
                  <SelectItem value="Refusée">Refusée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmit}>
                {editingOffre ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
